import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { AppState } from 'react-native';

import { fetchMusicLibrary, putMusicLibrary } from '../api/client';
import { subscribeCallBusy, getActiveCall } from '../calls/activeCallRegistry';
import { useLocale } from '../i18n/LocaleContext';
import { showToast } from '../util/toastBridge';
import {
  DEFAULT_EQUALIZER,
  equalizerPreampDb,
  isEqualizerTransparent,
  replayGainDb,
  type EqualizerState,
} from './equalizer';
import { createMusicEngine, MusicEngineHost } from './musicEngine';
import type { MusicEngine } from './musicEngineTypes';
import {
  EMPTY_CLOUD_STATE,
  applyCloudPayload,
  buildCloudPayload,
  countRestored,
  decideSync,
  ghostOf,
  mergeCloudPayloads,
  parseCloudPayload,
  payloadHash,
  type CloudState,
  type TrackGhost,
} from './musicCloud';
import {
  artworkPath,
  deleteTrackFiles,
  fallbackArtworkPath,
  loadLibrary,
  pickAndImport,
  playableUri,
  saveLibrary,
  savePlayerState,
  setLibraryOwner,
  type ImportResult,
} from './musicLibrary';
import { applyTrackEdit, groupTrackIds, isGroupSource, type TrackEdit } from './musicGroups';
import {
  acquireAudioFocus,
  hideNowPlaying,
  releaseAudioFocus,
  showNowPlaying,
  subscribeInterruptions,
  subscribeRemote,
} from './musicSystem';
import type { MusicTrack, PlaybackStatus, PlayerPersistedState, Playlist, RepeatMode } from './musicTypes';
import {
  addToPlaylist as addToPlaylistPure,
  createPlaylist as createPlaylistPure,
  deletePlaylist as deletePlaylistPure,
  forgetTrack,
  removeFromPlaylist as removeFromPlaylistPure,
  renamePlaylist as renamePlaylistPure,
} from './playlists';
import { cycleRepeat, nextIndex, previousAction, shuffleOrder } from './playerQueue';

/**
 * Состояние музыкального плеера на всё приложение.
 *
 * <p>Провайдер стоит в корне, рядом с навигацией: музыка не принадлежит ни одному экрану —
 * она играет при закрытом плеере, в чате, в фоне и при погашенном экране.
 *
 * <p><b>Позиция — отдельно от остального.</b> Она меняется несколько раз в секунду; положи её
 * в общий контекст — и вместе с ней перерисовывался бы весь список треков. Поэтому позиция
 * живёт в маленьком внешнем хранилище ({@link usePlaybackPosition}), и подписаны на неё только
 * полоса прогресса и время.
 */

type PlayerContextValue = {
  ready: boolean;
  engineAvailable: boolean;
  tracks: MusicTrack[];
  playlists: Playlist[];
  /**
   * Треки из описи на сервере, файлов которых на этом устройстве нет (после переустановки или
   * с другого телефона). Добавишь тот же файл — трек сам встанет на свои места в плейлистах.
   */
  ghosts: TrackGhost[];
  queue: string[];
  /** Откуда собрана очередь: плейлист или `null` — «Вся музыка». */
  sourceId: string | null;
  current: MusicTrack | null;
  status: PlaybackStatus;
  shuffle: boolean;
  repeat: RepeatMode;
  normalizeVolume: boolean;
  equalizer: EqualizerState;
  importing: { done: number; total: number } | null;
  engine: MusicEngine;
  /** Играть трек; очередь собирается из списка, в котором его нажали. */
  playTrack: (id: string, sourceId: string | null) => void;
  /** Слушать список целиком — по порядку или перемешав. */
  playList: (sourceId: string | null, shuffled: boolean) => void;
  togglePlay: () => void;
  next: () => void;
  previous: () => void;
  seek: (sec: number) => void;
  toggleShuffle: () => void;
  toggleRepeat: () => void;
  toggleNormalize: () => void;
  setEqualizer: (state: EqualizerState) => void;
  /** `restored` — сколько из добавленных треков вернулось на свои места в плейлистах. */
  importTracks: () => Promise<(ImportResult & { restored: number }) | null>;
  /** Забыть трек, которого нет на устройстве: убрать отовсюду, включая опись на сервере. */
  forgetGhost: (id: string) => void;
  /**
   * Удалить файл трека с устройства. Трек становится призраком и остаётся в плейлистах — здесь
   * и на других устройствах, где файл есть; `everywhere` — убрать его и из плейлистов, и из описи.
   */
  removeTrack: (id: string, everywhere?: boolean) => Promise<void>;
  /** Поправить название, исполнителя или альбом — одному треку или целой группе разом. */
  editTracks: (ids: readonly string[], edit: TrackEdit) => void;
  /** Свериться с описью на сервере сейчас, не дожидаясь возврата в приложение. */
  syncNow: () => Promise<void>;
  createPlaylist: (name: string, trackIds?: string[]) => string;
  renamePlaylist: (id: string, name: string) => void;
  deletePlaylist: (id: string) => void;
  addToPlaylist: (id: string, trackIds: string[]) => void;
  removeFromPlaylist: (id: string, trackId: string) => void;
};

const PlayerContext = createContext<PlayerContextValue | null>(null);

// ------------------------------------------------------------ позиция

type PositionSnapshot = { sec: number; at: number; playing: boolean };
let positionSnapshot: PositionSnapshot = { sec: 0, at: 0, playing: false };
const positionListeners = new Set<() => void>();

function publishPosition(sec: number, playing: boolean): void {
  positionSnapshot = { sec, at: Date.now(), playing };
  positionListeners.forEach((l) => l());
}

function subscribePosition(l: () => void): () => void {
  positionListeners.add(l);
  return () => {
    positionListeners.delete(l);
  };
}

/**
 * Позиция с отметкой времени. Движок сообщает её не чаще нескольких раз в секунду; плавность
 * полосе даёт интерполяция `sec + (now − at)`, пока трек играет.
 */
export function usePlaybackPosition(): PositionSnapshot {
  return useSyncExternalStore(subscribePosition, () => positionSnapshot);
}

export function interpolatePosition(p: PositionSnapshot, durationSec: number, now = Date.now()): number {
  const sec = p.playing ? p.sec + (now - p.at) / 1000 : p.sec;
  return durationSec > 0 ? Math.min(sec, durationSec) : sec;
}

// ------------------------------------------------------------ провайдер

const SAVE_DEBOUNCE_MS = 800;
/** Опись отправляется не сразу после правки: переименование и пара перестановок — одна запись. */
const CLOUD_PUSH_DEBOUNCE_MS = 3000;
const POSITION_SAVE_EVERY_MS = 10_000;
/** О сбое сохранения фонотеки говорим не чаще этого — сохранение идёт после каждой правки. */
const SAVE_FAILED_TOAST_EVERY_MS = 60_000;
/** Столько битых файлов подряд пропускаем при автопереходе, дальше — пауза: вдруг битое всё. */
const MAX_SKIPS_IN_ROW = 3;

/**
 * @param userId чья фонотека: у каждого аккаунта своя, и опись на сервере тоже его. `null` —
 *     пользователь ещё не известен; тогда плеер работает, но с сервером не говорит.
 */
export function MusicPlayerProvider({ children, userId }: { children: React.ReactNode; userId: string | null }) {
  const [engine] = useState<MusicEngine>(() => createMusicEngine());
  const { t } = useLocale();
  /**
   * Перевод для обработчиков, которые не должны пересоздаваться со сменой языка: от `persist`
   * зависит уборка при размонтировании, и новая его копия отпустила бы движок посреди песни.
   */
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);
  const [ready, setReady] = useState(false);
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [ghosts, setGhosts] = useState<TrackGhost[]>([]);
  const [queue, setQueue] = useState<string[]>([]);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [status, setStatus] = useState<PlaybackStatus>('idle');
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>('all');
  const [normalizeVolume, setNormalizeVolume] = useState(true);
  const [equalizer, setEqualizerState] = useState<EqualizerState>(DEFAULT_EQUALIZER);
  const [importing, setImporting] = useState<{ done: number; total: number } | null>(null);
  /** Растёт при перемотке: экран блокировки должен узнать новую позицию, а не досчитывать старую. */
  const [seekRev, setSeekRev] = useState(0);

  /** Последние значения — для обработчиков движка и системы, которые живут дольше рендера. */
  const latest = useRef({ tracks, playlists, ghosts, queue, sourceId, currentId, status, repeat, shuffle, normalizeVolume, equalizer });
  useEffect(() => {
    latest.current = { tracks, playlists, ghosts, queue, sourceId, currentId, status, repeat, shuffle, normalizeVolume, equalizer };
  });
  /** Что знаем об описи на сервере: её версия и отпечаток последней отправленной/принятой. */
  const cloud = useRef<CloudState>(EMPTY_CLOUD_STATE);

  /** Порядок треков списка: вся фонотека или плейлист. */
  const sourceOrder = useCallback((source: string | null): string[] => {
    const s = latest.current;
    if (source === null) {
      return s.tracks.map((t) => t.id);
    }
    const group = groupTrackIds(source, s.tracks);
    if (group) {
      return group.length > 0 ? group : s.tracks.map((t) => t.id);
    }
    const playlist = s.playlists.find((p) => p.id === source);
    if (!playlist) {
      return s.tracks.map((t) => t.id);
    }
    // Призраки в очередь не идут: играть нечего, и очередь споткнулась бы на первом же.
    const have = new Set(s.tracks.map((t) => t.id));
    return playlist.trackIds.filter((id) => have.has(id));
  }, []);
  /** Пауза, которую поставили звонок или система, а не человек: после неё можно продолжить. */
  const pausedBySystem = useRef(false);
  /** Трек, уже переданный движку, — чтобы «играть» не перезагружало файл. */
  const loadedId = useRef<string | null>(null);

  const current = useMemo(() => tracks.find((t) => t.id === currentId) ?? null, [tracks, currentId]);

  // ---- загрузка фонотеки
  useEffect(() => {
    let alive = true;
    setLibraryOwner(userId ?? 'default');
    void loadLibrary().then((lib) => {
      if (!alive) {
        return;
      }
      cloud.current = lib.cloud;
      setTracks(lib.tracks);
      setPlaylists(lib.playlists);
      setGhosts(lib.ghosts);
      setEqualizerState(lib.equalizer);
      const p = lib.player;
      const known = new Set(lib.tracks.map((t) => t.id));
      if (p) {
        const q = p.queue.filter((id) => known.has(id));
        setQueue(q.length > 0 ? q : lib.tracks.map((t) => t.id));
        setCurrentId(p.currentId && known.has(p.currentId) ? p.currentId : null);
        const keepSource =
          p.sourceId &&
          (lib.playlists.some((pl) => pl.id === p.sourceId) || (groupTrackIds(p.sourceId, lib.tracks)?.length ?? 0) > 0);
        setSourceId(keepSource ? (p.sourceId ?? null) : null);
        setShuffle(p.shuffle);
        setRepeat(p.repeat);
        setNormalizeVolume(p.normalizeVolume);
        publishPosition(p.positionSec, false);
      } else {
        setQueue(lib.tracks.map((t) => t.id));
      }
      setReady(true);
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  // ---- сохранение
  const playerState = useCallback((): PlayerPersistedState => {
    const s = latest.current;
    return {
      queue: s.queue,
      sourceId: s.sourceId,
      currentId: s.currentId,
      // Пока трек не передан движку, его позиция — ноль, а не «где остановились».
      positionSec: loadedId.current ? engine.getPosition() : positionSnapshot.sec,
      shuffle: s.shuffle,
      repeat: s.repeat,
      normalizeVolume: s.normalizeVolume,
    };
  }, [engine]);

  /** Провайдер размонтирован — сказать о сбое сохранения уже некому. */
  const unmounted = useRef(false);
  /** Когда последний раз говорили о сбое сохранения: не на каждую запись (UX-45). */
  const saveFailToastAt = useRef(0);

  // Именованное выражение: «Повторить» в тосте зовёт саму функцию.
  const persist = useCallback(function persistImpl() {
    const s = latest.current;
    void saveLibrary({
      tracks: s.tracks,
      playlists: s.playlists,
      ghosts: s.ghosts,
      cloud: cloud.current,
      player: playerState(),
      equalizer: s.equalizer,
    }).catch(() => {
      // Раньше сбой глотался молча: человек правил фонотеку, а после перезапуска правок не было.
      // Сохранение идёт после каждой правки, поэтому и о сбое — не чаще раза в минуту.
      const now = Date.now();
      if (unmounted.current || now - saveFailToastAt.current < SAVE_FAILED_TOAST_EVERY_MS) {
        return;
      }
      saveFailToastAt.current = now;
      showToast({
        message: tRef.current('musicSaveFailed'),
        actionLabel: tRef.current('musicRetry'),
        onAction: () => persistImpl(),
      });
    });
  }, [playerState]);

  /** Только позиция и очередь — маленький файл, без всей описи. */
  const persistPosition = useCallback(() => {
    void savePlayerState(playerState()).catch(() => undefined);
  }, [playerState]);

  useEffect(() => {
    if (!ready) {
      return;
    }
    const t = setTimeout(persist, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [ready, persist, tracks, playlists, ghosts, queue, sourceId, currentId, shuffle, repeat, normalizeVolume, equalizer]);

  // ---- опись на сервере (см. musicCloud.ts)
  const cloudPayload = useCallback(() => {
    const s = latest.current;
    return buildCloudPayload({
      tracks: s.tracks,
      ghosts: s.ghosts,
      playlists: s.playlists,
      equalizer: s.equalizer,
      settings: { shuffle: s.shuffle, repeat: s.repeat, normalizeVolume: s.normalizeVolume },
    });
  }, []);
  const syncing = useRef(false);
  /** Правка пришла, пока шла синхронизация, — её надо отправить сразу после. */
  const resyncWanted = useRef(false);
  const syncAgain = useRef<(() => Promise<void>) | null>(null);
  /** Только что приняли опись с сервера: следующее состояние — это она, отправлять нечего. */
  const adoptRemote = useRef(false);

  const syncNow = useCallback(async () => {
    if (!userId) {
      return;
    }
    if (syncing.current) {
      resyncWanted.current = true;
      return;
    }
    syncing.current = true;
    try {
      // Сначала спрашиваем, а потом пишем: так правка с другого телефона не затирается вслепую.
      const remote = await fetchMusicLibrary();
      const payload = cloudPayload();
      const hash = payloadHash(payload);
      const decision = decideSync(
        {
          revision: cloud.current.revision,
          dirty: hash !== cloud.current.syncedHash,
          hasContent: payload.tracks.length > 0 || payload.playlists.length > 0,
        },
        remote,
      );
      if ((decision === 'pull' || decision === 'merge') && remote) {
        const parsed = parseCloudPayload(remote.payload);
        if (!parsed) {
          // Опись в формате новее нашего — не трогаем и не перезаписываем: её писал клиент новее.
          cloud.current = { revision: remote.revision, syncedHash: hash, base: cloud.current.base ?? null };
          return;
        }
        // Правили и здесь, и там (например, в браузере добавили песни в плейлист, а на телефоне
        // тем временем переименовали другой) — сливаем, а не затираем одну сторону другой.
        const base = cloud.current.base ? parseCloudPayload(cloud.current.base) : null;
        const incoming = decision === 'merge' ? mergeCloudPayloads(base, payload, parsed) : parsed;
        const s = latest.current;
        const applied = applyCloudPayload(s.tracks, incoming);
        const onServer = new Set(parsed.tracks.map((t) => t.id));
        // Слитое надо отправить; принятое как есть — только если у нас есть файлы не из описи.
        adoptRemote.current = decision === 'pull' && s.tracks.every((t) => onServer.has(t.id));
        cloud.current = { revision: remote.revision, syncedHash: '', base: parsed };
        setTracks(applied.tracks);
        setGhosts(applied.ghosts);
        setPlaylists(applied.playlists);
        setEqualizerState(applied.equalizer);
        setShuffle(applied.settings.shuffle);
        setRepeat(applied.settings.repeat);
        setNormalizeVolume(applied.settings.normalizeVolume);
      } else if (decision === 'push') {
        const revision = await putMusicLibrary(payload);
        cloud.current = { revision, syncedHash: hash, base: payload };
        persist();
      }
    } catch {
      // Нет сети или сервер недоступен — опись подождёт: следующая правка или возврат в
      // приложение попробуют снова, а на телефоне всё уже сохранено.
    } finally {
      syncing.current = false;
      if (resyncWanted.current) {
        resyncWanted.current = false;
        setTimeout(() => void syncAgain.current?.(), 0);
      }
    }
  }, [userId, cloudPayload, persist]);

  useEffect(() => {
    syncAgain.current = syncNow;
  }, [syncNow]);

  useEffect(() => {
    if (ready) {
      void syncNow();
    }
  }, [ready, syncNow]);

  useEffect(() => {
    if (!ready || !userId) {
      return;
    }
    const hash = payloadHash(cloudPayload());
    if (adoptRemote.current) {
      adoptRemote.current = false;
      cloud.current = { ...cloud.current, syncedHash: hash };
      persist();
      return;
    }
    if (hash === cloud.current.syncedHash) {
      return;
    }
    const t = setTimeout(() => void syncNow(), CLOUD_PUSH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [ready, userId, tracks, ghosts, playlists, equalizer, shuffle, repeat, normalizeVolume, cloudPayload, syncNow, persist]);

  useEffect(() => {
    // Вернулись в приложение — вдруг опись поменяли с другого телефона.
    const sub = AppState.addEventListener('change', (st) => {
      if (st === 'active') {
        void syncNow();
      }
    });
    return () => sub.remove();
  }, [syncNow]);

  useEffect(() => {
    if (status !== 'playing') {
      return;
    }
    const timer = setInterval(persistPosition, POSITION_SAVE_EVERY_MS);
    return () => clearInterval(timer);
  }, [status, persistPosition]);

  useEffect(() => {
    // Уход в фон — повод записать, где остановились: приложение могут выгрузить в любой момент.
    const sub = AppState.addEventListener('change', (s) => {
      if (s !== 'active') {
        persist();
      }
    });
    return () => sub.remove();
  }, [persist]);

  // ---- звук: громкость трека и эквалайзер
  useEffect(() => {
    const transparent = isEqualizerTransparent(equalizer);
    engine.setEqualizer(transparent ? null : equalizer.gains);
    const rg = normalizeVolume ? replayGainDb(current?.replayGainDb, current?.replayGainPeak) : 0;
    engine.setTrackGainDb(rg + equalizerPreampDb(equalizer));
  }, [engine, equalizer, normalizeVolume, current]);

  // ---- воспроизведение
  const startTrack = useCallback(
    async (id: string, autoplay: boolean, startAt = 0) => {
      const track = latest.current.tracks.find((t) => t.id === id);
      if (!track) {
        return;
      }
      if (autoplay && getActiveCall()) {
        // Во время разговора музыка не играет: она звучала бы поверх собеседника.
        return;
      }
      setCurrentId(id);
      setStatus(autoplay ? 'loading' : 'paused');
      publishPosition(startAt, false);
      if (autoplay) {
        acquireAudioFocus();
      }
      try {
        const uri = await playableUri(track);
        loadedId.current = id;
        if (!engine.load(uri, startAt, autoplay)) {
          // Движка нет или граф не собрался — не делаем вид, что играет.
          loadedId.current = null;
          setStatus('paused');
          return;
        }
        if (autoplay) {
          setStatus('playing');
          publishPosition(startAt, true);
        }
      } catch {
        setStatus('idle');
      }
    },
    [engine],
  );

  /**
   * Во время разговора музыка не играет. Раньше нажатие просто ничего не делало — человек
   * не понимал почему (UX-45). Говорим только о нажатии: продолжение после прерывания или
   * звонка молча ждёт своего часа.
   */
  const notifyBlockedByCall = useCallback(() => {
    showToast({ message: tRef.current('musicBlockedByCall') });
  }, []);

  const resume = useCallback((fromUser: boolean) => {
    const s = latest.current;
    const id = s.currentId ?? s.queue[0];
    if (!id) {
      return;
    }
    if (getActiveCall()) {
      if (fromUser) {
        notifyBlockedByCall();
      }
      return;
    }
    pausedBySystem.current = false;
    acquireAudioFocus();
    if (loadedId.current !== id) {
      // После перезапуска движок ещё пуст, и его позиция — ноль; где остановились, помнит
      // снимок позиции, восстановленный из фонотеки.
      void startTrack(id, true, id === s.currentId ? positionSnapshot.sec : 0);
      return;
    }
    void engine.play();
    setStatus('playing');
    publishPosition(engine.getPosition(), true);
  }, [engine, notifyBlockedByCall, startTrack]);

  const play = useCallback(() => resume(true), [resume]);
  /** Продолжить по решению системы (кончился звонок, вернули аудиофокус) — без тоста. */
  const resumeBySystem = useCallback(() => resume(false), [resume]);

  const pause = useCallback(
    (bySystem = false) => {
      if (latest.current.status !== 'playing' && latest.current.status !== 'loading') {
        return;
      }
      pausedBySystem.current = bySystem;
      engine.pause();
      setStatus('paused');
      publishPosition(engine.getPosition(), false);
    },
    [engine],
  );

  const togglePlay = useCallback(() => {
    if (latest.current.status === 'playing' || latest.current.status === 'loading') {
      pause();
    } else {
      play();
    }
  }, [pause, play]);

  const advance = useCallback(
    (auto: boolean) => {
      const s = latest.current;
      const index = s.currentId ? s.queue.indexOf(s.currentId) : -1;
      const n = nextIndex(s.queue.length, index, s.repeat, auto);
      if (n === null) {
        setStatus('paused');
        engine.seek(0);
        publishPosition(0, false);
        setSeekRev((r) => r + 1);
        return;
      }
      const keepPlaying = auto || s.status === 'playing' || s.status === 'loading';
      void startTrack(s.queue[n], keepPlaying);
    },
    [engine, startTrack],
  );

  const next = useCallback(() => advance(false), [advance]);

  const previous = useCallback(() => {
    const s = latest.current;
    const index = s.currentId ? s.queue.indexOf(s.currentId) : -1;
    const action = previousAction(s.queue.length, Math.max(0, index), engine.getPosition(), s.repeat);
    if (action.kind === 'restart') {
      engine.seek(0);
      publishPosition(0, s.status === 'playing');
      setSeekRev((r) => r + 1);
      return;
    }
    void startTrack(s.queue[action.index], s.status === 'playing' || s.status === 'loading');
  }, [engine, startTrack]);

  const seek = useCallback(
    (sec: number) => {
      engine.seek(sec);
      publishPosition(sec, latest.current.status === 'playing');
      setSeekRev((r) => r + 1);
    },
    [engine],
  );

  const playTrack = useCallback(
    (id: string, source: string | null) => {
      const s = latest.current;
      if (id === s.currentId && source === s.sourceId) {
        togglePlay();
        return;
      }
      if (getActiveCall()) {
        // Тап по треку посреди разговора: очередь не трогаем, говорим, почему тишина (UX-45).
        notifyBlockedByCall();
        return;
      }
      const order = sourceOrder(source);
      setSourceId(source);
      setQueue(s.shuffle ? shuffleOrder(order, id) : order);
      pausedBySystem.current = false;
      void startTrack(id, true);
    },
    [notifyBlockedByCall, startTrack, togglePlay, sourceOrder],
  );

  const stop = useCallback(() => {
    engine.stop();
    loadedId.current = null;
    setStatus('idle');
    publishPosition(engine.getPosition(), false);
    void hideNowPlaying();
    releaseAudioFocus();
  }, [engine]);

  // ---- события движка
  /** Сколько файлов подряд не открылось — сбрасывается, как только трек пошёл. */
  const skipsInRow = useRef(0);

  useEffect(() => {
    engine.setEvents({
      onPosition: (sec) => {
        if (sec > 0) {
          skipsInRow.current = 0;
        }
        publishPosition(sec, latest.current.status === 'playing');
      },
      onEnded: () => advance(true),
      onError: (e) => {
        const s = latest.current;
        const wasPlaying = s.status === 'playing' || s.status === 'loading';
        setStatus('paused');
        if (e.message === 'engine-unavailable') {
          // Плеер сам покажет, что нужно обновить приложение.
          return;
        }
        const track = s.tracks.find((x) => x.id === s.currentId);
        if (track) {
          showToast({ message: t('musicPlaybackFailed').replace('{title}', track.title) });
        }
        // Битый или неподдерживаемый файл не должен молча останавливать альбом.
        if (wasPlaying && skipsInRow.current < MAX_SKIPS_IN_ROW) {
          skipsInRow.current += 1;
          // При повторе одного трека автопереход вернул бы тот же битый файл.
          advance(s.repeat !== 'one');
        }
      },
    });
  }, [engine, advance, t]);

  // ---- звонки, прерывания, экран блокировки
  useEffect(
    () =>
      subscribeCallBusy((busy) => {
        if (busy) {
          pause(true);
        } else if (pausedBySystem.current) {
          // Разговор кончился, а музыку остановил именно он — продолжаем с того же места.
          resumeBySystem();
        }
      }),
    [pause, resumeBySystem],
  );

  useEffect(
    () =>
      subscribeInterruptions({
        pause: () => pause(true),
        resume: () => (pausedBySystem.current ? resumeBySystem() : undefined),
        // Пауза от наушников — как от человека: после звонка музыка не должна пойти в динамик.
        unplugged: () => pause(),
      }),
    [pause, resumeBySystem],
  );

  useEffect(
    () =>
      subscribeRemote({
        play,
        pause: () => pause(),
        next,
        previous,
        seekTo: seek,
        stop,
      }),
    [play, pause, next, previous, seek, stop],
  );

  useEffect(() => {
    if (!current || status === 'idle') {
      return;
    }
    let alive = true;
    void (async () => {
      // Без обложки — синяя нота плеера, а не чёрный квадрат в Dynamic Island.
      const art = artworkPath(current) ?? (await fallbackArtworkPath());
      if (!alive) {
        return;
      }
      await showNowPlaying({
        title: current.title,
        artist: current.artist,
        album: current.album,
        artworkPath: art,
        durationSec: current.durationSec,
        elapsedSec: engine.getPosition(),
        playing: status === 'playing' || status === 'loading',
      });
    })();
    return () => {
      alive = false;
    };
  }, [engine, current, status, seekRev]);

  // Провайдер живёт, пока человек вошёл: выход из аккаунта останавливает музыку и убирает её
  // со шторки, иначе уведомление «играет» висело бы над экраном входа.
  // Звуковой контекст и граф отпускаются насовсем: провайдер пересоздаётся при смене аккаунта
  // (`key` в App.tsx), и прежний работающий AudioContext иначе жил бы до закрытия вкладки (LEAK-6).
  useEffect(
    () => () => {
      persist();
      unmounted.current = true;
      engine.dispose();
      void hideNowPlaying();
      releaseAudioFocus();
    },
    [engine, persist],
  );

  // ---- фонотека
  const importTracks = useCallback(async () => {
    setImporting({ done: 0, total: 0 });
    try {
      const ghostsBefore = latest.current.ghosts;
      const res = await pickAndImport(latest.current.tracks, (done, total) => setImporting({ done, total }));
      if (!res) {
        return null;
      }
      const restored = countRestored(res.added, ghostsBefore);
      if (res.added.length > 0) {
        const addedIds = new Set(res.added.map((t) => t.id));
        setTracks((prev) => [...prev, ...res.added]);
        // Вернувшийся файл перестаёт быть призраком — он уже стоит в своих плейлистах по id.
        setGhosts((prev) => prev.filter((g) => !addedIds.has(g.id)));
        // Новые треки попадают в очередь, только если она собрана из всей фонотеки.
        if (latest.current.sourceId === null) {
          setQueue((prev) => [...prev, ...res.added.map((t) => t.id)]);
        }
      }
      return { ...res, restored };
    } finally {
      setImporting(null);
    }
  }, []);

  const removeTrack = useCallback(
    async (id: string, everywhere = false) => {
      const track = latest.current.tracks.find((t) => t.id === id);
      if (!track) {
        return;
      }
      if (latest.current.currentId === id) {
        stop();
        setCurrentId(null);
      }
      setTracks((prev) => prev.filter((x) => x.id !== id));
      if (everywhere) {
        setPlaylists((prev) => forgetTrack(prev, id));
        setGhosts((prev) => prev.filter((g) => g.id !== id));
      } else {
        // Освободить место на телефоне не значит выкинуть трек из плейлистов на всех устройствах:
        // он остаётся призраком и сам встанет на место, если файл добавят снова.
        setGhosts((prev) => [...prev.filter((g) => g.id !== id), ghostOf(track)]);
      }
      setQueue((prev) => prev.filter((q) => q !== id));
      await deleteTrackFiles(track);
    },
    [stop],
  );

  const editTracks = useCallback((ids: readonly string[], edit: TrackEdit) => {
    const targets = new Set(ids);
    // Правятся и призраки: трек, добавленный на другом устройстве, тоже можно подписать.
    setTracks((prev) => prev.map((t) => (targets.has(t.id) ? applyTrackEdit(t, edit) : t)));
    setGhosts((prev) => prev.map((g) => (targets.has(g.id) ? applyTrackEdit(g, edit) : g)));
    const s = latest.current;
    if (isGroupSource(s.sourceId) && (edit.artist !== undefined || edit.album !== undefined)) {
      // Очередь была из исполнителя или альбома, которого под прежним именем больше нет:
      // музыка играет дальше, а очередь становится всей фонотекой.
      setSourceId(null);
    }
  }, []);

  const toggleShuffle = useCallback(() => {
    const s = latest.current;
    const order = sourceOrder(s.sourceId);
    setQueue(s.shuffle ? order : shuffleOrder(order, s.currentId));
    setShuffle(!s.shuffle);
  }, [sourceOrder]);

  const playList = useCallback(
    (source: string | null, shuffled: boolean) => {
      const order = sourceOrder(source);
      if (order.length === 0) {
        return;
      }
      const first = shuffled ? order[Math.floor(Math.random() * order.length)] : order[0];
      setShuffle(shuffled);
      setSourceId(source);
      setQueue(shuffled ? shuffleOrder(order, first) : order);
      pausedBySystem.current = false;
      void startTrack(first, true);
    },
    [sourceOrder, startTrack],
  );

  const forgetGhost = useCallback((id: string) => {
    setGhosts((prev) => prev.filter((g) => g.id !== id));
    setPlaylists((prev) => forgetTrack(prev, id));
  }, []);

  // ---- плейлисты
  const createPlaylist = useCallback((name: string, trackIds: string[] = []) => {
    const { list, id } = createPlaylistPure(latest.current.playlists, name, trackIds);
    setPlaylists(list);
    return id;
  }, []);

  const renamePlaylist = useCallback((id: string, name: string) => {
    setPlaylists((prev) => renamePlaylistPure(prev, id, name));
  }, []);

  const deletePlaylist = useCallback((id: string) => {
    setPlaylists((prev) => deletePlaylistPure(prev, id));
    if (latest.current.sourceId === id) {
      // Очередь была из этого плейлиста — музыка не обрывается, очередь становится всей фонотекой.
      setSourceId(null);
      setQueue(latest.current.tracks.map((t) => t.id));
    }
  }, []);

  const addToPlaylist = useCallback((id: string, trackIds: string[]) => {
    setPlaylists((prev) => addToPlaylistPure(prev, id, trackIds));
    if (latest.current.sourceId === id) {
      setQueue((prev) => [...prev, ...trackIds.filter((t) => !prev.includes(t))]);
    }
  }, []);

  const removeFromPlaylist = useCallback((id: string, trackId: string) => {
    setPlaylists((prev) => removeFromPlaylistPure(prev, id, trackId));
    const s = latest.current;
    if (s.sourceId === id) {
      // Играющий трек доигрывает: убрать его из списка не значит оборвать песню.
      setQueue((prev) => prev.filter((q) => q !== trackId || q === s.currentId));
    }
  }, []);

  const toggleRepeat = useCallback(() => setRepeat((r) => cycleRepeat(r)), []);
  const toggleNormalize = useCallback(() => setNormalizeVolume((v) => !v), []);

  const value = useMemo<PlayerContextValue>(
    () => ({
      ready,
      engineAvailable: engine.available(),
      tracks,
      playlists,
      ghosts,
      queue,
      sourceId,
      current,
      status,
      shuffle,
      repeat,
      normalizeVolume,
      equalizer,
      importing,
      engine,
      playTrack,
      playList,
      togglePlay,
      next,
      previous,
      seek,
      toggleShuffle,
      toggleRepeat,
      toggleNormalize,
      setEqualizer: setEqualizerState,
      importTracks,
      forgetGhost,
      removeTrack,
      editTracks,
      syncNow,
      createPlaylist,
      renamePlaylist,
      deletePlaylist,
      addToPlaylist,
      removeFromPlaylist,
    }),
    [
      ready,
      engine,
      tracks,
      playlists,
      ghosts,
      queue,
      sourceId,
      current,
      status,
      shuffle,
      repeat,
      normalizeVolume,
      equalizer,
      importing,
      playTrack,
      playList,
      togglePlay,
      next,
      previous,
      seek,
      toggleShuffle,
      toggleRepeat,
      toggleNormalize,
      importTracks,
      forgetGhost,
      removeTrack,
      editTracks,
      syncNow,
      createPlaylist,
      renamePlaylist,
      deletePlaylist,
      addToPlaylist,
      removeFromPlaylist,
    ],
  );

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <MusicEngineHost engine={engine} />
    </PlayerContext.Provider>
  );
}

export function useMusicPlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    throw new Error('useMusicPlayer must be used inside MusicPlayerProvider');
  }
  return ctx;
}

/** Для кнопки в шапке: она может стоять и там, где провайдера нет (тесты, стенды). */
export function useMusicPlayerOptional(): PlayerContextValue | null {
  return useContext(PlayerContext);
}
